import { IWorkflowTemplate, IWorkflowTemplateVersion } from '@commenda/types';
import { WorkflowTemplateVersionStatus } from '@prisma/client';

/** Whether the template read behind the live state and the warnings has landed. */
export type TWorkflowActivationReadState = 'loading' | 'error' | 'ready';

/** Rules for making a published workflow version the one new runs use. */
export class WorkflowVersionActivationHelper {
  static readonly ACTIVATION_FAILURE_MESSAGE =
    'Published, but failed to make this version live. New runs cannot use it yet.';

  /** The template's own record wins: it is re-read after every activation, while the version's
   * embedded template is only as fresh as the version read. */
  static isActive(
    version: IWorkflowTemplateVersion,
    template: IWorkflowTemplate | null
  ): boolean {
    const activeVersionId =
      template?.activeVersionId ?? version.workflowTemplate?.activeVersionId;

    return Boolean(activeVersionId) && activeVersionId === version.id;
  }

  static getReadState(
    isLoading: boolean,
    error: unknown,
    template: IWorkflowTemplate | null
  ): TWorkflowActivationReadState {
    if (isLoading) return 'loading';
    return error || !template ? 'error' : 'ready';
  }

  /** What an author should know before going live, shown on the publish popover's second step. */
  static getActivationWarnings(
    version: IWorkflowTemplateVersion,
    template: IWorkflowTemplate | null
  ): string[] {
    const siblings = template?.versions ?? [];

    const hasNewerPublished = siblings.some(
      (sibling) =>
        sibling.id !== version.id &&
        sibling.status === WorkflowTemplateVersionStatus.PUBLISHED &&
        new Date(sibling.createdAt) > new Date(version.createdAt)
    );
    // Without the template (still loading, or failed) the active version is unknown, not changed.
    const isSourceNoLongerActive =
      template !== null &&
      Boolean(version.starterWorkflowTemplateVersionId) &&
      template.activeVersionId !== version.starterWorkflowTemplateVersionId;

    return [
      hasNewerPublished
        ? 'A newer published version of this workflow already exists.'
        : null,
      isSourceNoLongerActive
        ? 'The version this draft was copied from is no longer the active one.'
        : null,
      "Going live replaces this workflow's trigger subscriptions with the ones in this version.",
    ].filter((warning): warning is string => warning !== null);
  }
}
